import { use, useEffect, useMemo } from 'react'

import { ChatContext } from '@app/context'
import { useAppDispatch } from '@app/hooks/useAppDispatch'
import { MessageItem } from '@app/models'
import { fetchConversationParticipantLabelsThunk } from '@app/store/participantLabels'

import { useParticipantLabels } from './useParticipantLabels'

export const useWhitelist = (conversationId: number, message: MessageItem) => {
  const { state: chatState } = use(ChatContext)
  const dispatch = useAppDispatch()
  const participantLabelsState = useParticipantLabels()

  const participant = useMemo(() => {
    return chatState.conversations[conversationId]?.participants.find(
      (p) => p.id === message.fromParticipantId
    )
  }, [chatState, conversationId, message.fromParticipantId])

  const participantId = participant?.id
  const participantLabels =
    (participantId && participantLabelsState.byParticipantId[participantId]) ||
    undefined
  const isConversationLabelsLoading =
    participantLabelsState.loadingByConversationId[conversationId]
  const hasConversationLabelsFailed =
    participantLabelsState.failedByConversationId[conversationId]

  useEffect(() => {
    if (
      conversationId &&
      !participantLabels &&
      !isConversationLabelsLoading &&
      !hasConversationLabelsFailed
    ) {
      dispatch(fetchConversationParticipantLabelsThunk(conversationId))
    }
  }, [
    conversationId,
    participantLabels,
    isConversationLabelsLoading,
    hasConversationLabelsFailed,
    dispatch,
  ])

  return useMemo(() => {
    const normalizedLabels = (participantLabels || []).map((label) =>
      label.toLowerCase()
    )

    return {
      isWhitelisted: normalizedLabels.includes('whitelist'),
      fetching: isConversationLabelsLoading && !participantLabels,
    }
  }, [participantLabels, isConversationLabelsLoading])
}
